/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Typography as Text } from '@mui/material';
import Link from '@mui/material/Link';

import { useStore, TOKEN } from '../../store';
import { addSuccessSnack, addErrorSnack } from '../../utils/snackbar';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Divider from '@mui/material/Divider';

import { track } from '../../utils/track';
import { useIsMobile } from '../../utils/hooks';

import { signup } from '../../actions/UserActions';

import CustomIdpSignInButton from './CustomIdpSignInButton';
import GoogleSignInButton from './GoogleSignInButton';
import MicrosoftSignInButton from './MicrosoftSignInButton';
import { useQueryClient } from 'react-query';
import { passwordPolicy } from '../../utils/password';
import PasswordField from '../common/PasswordField';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';

import { IS_SINGLE_TENENT_MODE, CUSTOM_IDP } from '../../constants';

export default function SignupForm({
  inviteToken,
  inviteEmail,
  referralToken,
}) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [, setToken] = useStore(TOKEN);
  const [show, setShow] = useState(false);
  const [showAlternate, setShowAlternate] = useState(false);

  const { isMobile, width } = useIsMobile();
  const navigate = useNavigate();
  const client = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const onSubmit = useCallback(
    async (data) => {
      setLoading(true);
      data.language = i18n?.language?.split('-')?.[0] || 'sv';
      if (inviteToken && inviteEmail) {
        data.email = inviteEmail;
        data.token = inviteToken;
      }
      if (referralToken) {
        data.referralToken = referralToken;
      }
      try {
        signup(data)
          .then((data) => {
            const { token, secure_login: secureLogin } = data;
            track('signup', { method: 'email' });
            if (secureLogin) {
              navigate('/secure-login/verify');
            } else {
              addSuccessSnack(t('Tack för att du registrerade dig!'));
              setToken(token);
              client.clear();
              navigate('/');
            }
          })
          .catch((err) => {
            if (err.response.data === 'E01') {
              setError(
                t(
                  'Denna mailadress har blivit inbjuden till en organisation. Vänligen kolla din email för en inbjudningslänk.'
                )
              );
            } else if (err.response.data === 'E02') {
              setError(
                t(
                  'Ditt lösenord uppfyller inte vår lösenordspolicy. Vänligen använd ett starkare lösenord.'
                )
              );
            } else {
              setError(null);
            }
            addErrorSnack(t('Något gick fel, försök igen!'));
          });
      } catch (error) {
        addErrorSnack(t('Något gick fel, försök igen!'));
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      setToken,
      i18n?.language,
      t,
      inviteToken,
      inviteEmail,
      referralToken,
      client,
    ]
  );

  const password = watch('password');

  const passwordPolicyRes = useMemo(() => {
    return passwordPolicy(password || '');
  }, [password]);

  const handleVisibility = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  if (IS_SINGLE_TENENT_MODE && CUSTOM_IDP && !showAlternate) {
    return (
      <div
        css={css`
          display: flex;
          padding: 1rem;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          max-width: 25rem;
        `}
      >
        <CustomIdpSignInButton
          customIdp={CUSTOM_IDP}
          inviteToken={inviteToken}
        />
        <Button
          variant="text"
          color="primary"
          css={css`
            margin-top: 1rem;
          `}
          onClick={() => setShowAlternate(true)}
        >
          {t('Logga in med email och lösenord')}
        </Button>
      </div>
    );
        
  }

  return (
    <div
      css={css`
        display: flex;
        padding: 1rem;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        max-width: 25rem;
      `}
    >
      {showAlternate && CUSTOM_IDP ? (
        <Button
          onClick={() => setShowAlternate(false)}
          variant="text"
          color="primary"
          css={css`
            margin-bottom: 1rem;
          `}
        >
          {t('Logga in med {{name}}', {
            name: CUSTOM_IDP.name,
          })}
        </Button>
      ) : null}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <GoogleSignInButton
            width={isMobile ? width - 64 : 395}
            text="signup_with"
            inviteToken={inviteToken}
            referralToken={referralToken}
          />
          <MicrosoftSignInButton
            width={isMobile ? width - 64 : 395}
            inviteToken={inviteToken}
            referralToken={referralToken}
          />
        </div>
        <Divider
          css={css`
            margin: 1rem 0 0;
          `}
          flexItem
        >
          <Text variant="body1" fontWeight="600">
            {t('Eller')}
          </Text>
        </Divider>
        {error ? (
          <Text variant="caption2" color="error">
            {error}
          </Text>
        ) : null}
        {inviteToken && inviteEmail ? (
          <TextField
            value={inviteEmail}
            fullWidth
            disabled
            margin="normal"
            label={t('Email')}
            type="email"
            id="email"
            sx={{ mb: 0 }}
          />
        ) : (
          <TextField
            fullWidth
            margin="normal"
            label={t('Email')}
            type="email"
            id="email"
            {...register('email', {
              required: t('Email är obligatoriskt'),
              pattern: {
                value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                message: t('Ogiltig email'),
              },
            })}
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            sx={{ mb: 0 }}
          />
        )}
        <TextField
          fullWidth
          margin="normal"
          label={t('Lösenord')}
          type={show ? 'text' : 'password'}
          id="password"
          {...register('password', {
            required: t('Lösenord är obligatoriskt'),
          })}
          error={Boolean(errors.password)}
          helperText={
            errors.password?.message || !passwordPolicyRes?.hasLength
              ? t(
                  'Lösenordet måste vara minst 8 tecken långt och minst en siffra'
                )
              : !passwordPolicyRes?.hasLetter
              ? t('Lösenordet måste innehålla minst en bokstav')
              : !passwordPolicyRes?.hasNumber
              ? t('Lösenordet måste innehålla minst en siffra')
              : ' '
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleVisibility}>
                  {show ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          css={css`
            margin-top: 1rem;
          `}
          disabled={loading || !passwordPolicyRes.allowed}
          size="large"
          fullWidth
        >
          {loading
            ? t('Skapar konto...')
            : inviteToken
            ? t('Skapa konto')
            : t('Skapa gratiskonto')}
        </Button>
      </form>
      <Text
        variant="caption"
        textAlign="center"
        fontWeight="400"
        fontSize="12"
        css={{ marginTop: '1rem' }}
      >
        {t('Genom att skapa ett konto godkänner du våra')}{' '}
        <Link
          href="https://app.klang.ai/info/terms.html"
          target="_blank"
          rel="noopener"
        >
          {t('Användarvillkor')}
        </Link>{' '}
        {t('och')}{' '}
        <Link
          href="https://app.klang.ai/info/privacy.html"
          target="_blank"
          rel="noopener"
        >
          {t('Integritetspolicy')}
        </Link>
      </Text>
    </div>
  );
}
